<template>
  <div :id="`lane-${laneIndex}-item-${laneItemIndex}`"
    class="szw-flex szw-flex-col szw-h-[88px] szw-rounded-2 szw-p-[6px] szw-justify-between szw-w-full szw-transition-all szw-duration-300 szw-relative szw-bg-latest-background szw-border szw-border-border szw-border-solid"
    :style="laneItemStyle" @click="emitLaneItemClick(laneItem)">
    <div
      class="szw-flex szw-left-0 -szw-top-[20px] szw-w-full szw-flex-row szw-absolute szw-items-center szw-gap-[10px] szw-z-50 szw-justify-center szw-px-7"
      v-if="hasThirdPlaceFinal && laneItem.stage === '3rd_place_final'">
      <span class="szw-flex szw-h-px szw-flex-1 szw-bg-text-icon-tertiary" />
      <span
        class="szw-flex szw-text-xxs szw-leading-3 szw-font-semibold szw-text-text-icon-tertiary szw-z-50 szw-text-nowrap">{{
          loc('sm_third_place').toUpperCase() }}</span>
      <span class="szw-flex szw-h-px szw-flex-1 szw-bg-text-icon-tertiary" />
    </div>

    <div class="szw-h-full szw-w-6 szw-absolute szw-left-0 szw-top-0" v-if="hasBracketLines">
      <span class="ko-bracket-y-line" :style="laneItemLineStyle" />
      <span class="ko-bracket-x-line" />
    </div>

    <div
      class="szw-flex szw-flex-row szw-h-4 szw-text-xxs szw-uppercase szw-font-medium szw-items-center szw-tracking-wider"
      v-if="event.teams">

      <div v-if="!event.allEventsEnded" class="szw-flex szw-w-full szw-items-center">
        <span class="szw-text-latest-text-icon-primary">{{ event.startTime ? helpers.formatDate(event.startTime) : 'TBD'
          }}</span>
        <div class="szw-text-[24px] szw-leading-[12px] szw-h-[16px] szw-px-1">&middot;</div>
        <div
          v-if="event.live"
          :class="['szw-flex szw-items-center szw-text-center szw-gap-1 szw-h-[20px] szw-bg-label-color-live szw-px-2 szw-py-0.5 szw-rounded szw-text-text-icon-secondary szw-leading-[12px] szw-mr-auto']"
        >
          <sprite sprite-id="live-indicator" />
          <span>{{ loc('sm_live', langId) }}</span>
        </div>
        <span v-else class="szw-mr-auto">{{ event.startTime ? helpers.getTime(new Date(event.startTime)) : 'TBD' }}</span>
        <span v-if="event.live" class="szw-text-right szw-w-[46px] szw-text-latest-text-icon-tertiary szw-mr-2">
          {{event.matchStatus }}
        </span>
        <span v-if="sportType !== 'basketball'" class="szw-text-center szw-w-[32px] szw-text-latest-text-icon-tertiary">{{ isSingleMatch ? event.matchStatus : loc('sm_knockout_agg', langId) }}</span>
        <span v-else class="szw-text-center szw-w-[32px] szw-text-latest-text-icon-tertiary">{{ isSingleMatch ? event.matchStatus : 'SER' }}</span>
      </div>
      <div v-else class="szw-flex szw-w-full">
        <span class="szw-text-latest-text-icon-primary">{{ loc('sm_knockout_final', langId) }}</span>
        <span v-if="sportType !== 'basketball'"  class="szw-text-center szw-w-[32px] szw-text-latest-text-icon-tertiary szw-ml-auto">{{ isSingleMatch ? event.matchStatus : loc('sm_knockout_agg', langId) }}</span>
        <span v-else class="szw-text-center szw-w-[32px] szw-text-latest-text-icon-tertiary szw-ml-auto">{{ isSingleMatch ? event.matchStatus : 'SER' }}</span>
      </div>
    </div>

    <div class="szw-flex szw-flex-row szw-gap-[4px] szw-justify-start szw-items-center szw-h-4" v-else>
      <sprite :sprite-id="adjustedStatus.spriteId" :color="adjustedStatus.color" class="szw-w-[16px] szw-h-[16px]" />
      <span class="szw-text-xxs szw-font-medium szw-uppercase szw-tracking-wider szw-leading-[12px]">{{
        adjustedStatus.statusLabel }}</span>
    </div>


    <div class="szw-flex szw-flex-col szw-gap-1" v-if="event.teams">
      <div
        :class="['szw-flex szw-flex-row szw-h-6 szw-w-full szw-items-center szw-gap-2', { 'szw-font-semibold': team.id === event.winnerId }]"
        v-for="team of event.aggregatedTeams || event.teams" :key="team.id"
      >
        <div class="szw-h-5 szw-w-5">
          <img v-if="team.logoUrl" :src="team.logoUrl" class="szw-h-5 szw-w-5" :alt="team.id" />
          <fallback-logo v-else :text="team.virtual ? '' : team.shortName" size="full" />
        </div>

        <span class="szw-truncate szw-mr-auto">{{ team.name }}</span>

        <!-- show  live result when match is live -->
        <div
          v-if="event.live && team.penaltyScore !== null"
          :class="[
            'szw-h-6 szw-w-[32px] szw-flex szw-items-center szw-justify-center szw-bg-latest-event-color-score szw-rounded-1 szw-text-xs szw-text-latest-text-icon-secondary szw-font-normal'
          ]"
        >
          <span>{{ team.penaltyScore ?? '-' }}</span>
        </div>

        <!-- show  live result penaltyScore when match is live and penalty shootout -->
        <div v-if="event.live && team.penaltyScore === null"
          :class="[
            'szw-h-6 szw-w-[32px] szw-flex szw-items-center szw-justify-center szw-bg-section-static szw-rounded-1 szw-text-xs'
          ]"
        >
          <span>{{ team.score ?? '-' }}</span>
        </div>

        <!-- aggregated result-->
        <div
          :class="[
            'szw-h-6 szw-w-[32px] szw-flex szw-items-center szw-justify-center szw-rounded-1 szw-text-xs',
            { 'szw-text-latest-text-icon-tertiary szw-font-medium': !event.aggregatedTeams },
            { 'szw-bg-section-static szw-text-latest-text-icon-primary': isSingleMatch },
            { 'szw-text-latest-color-primary': event.aggregatedTeams }
          ]"
        >
          <div v-if="isSingleMatch">
            <span>{{ team.score ?? '-' }}</span>
          </div>
          <div v-else>
            <span v-if="sportType === 'basketball'">{{ team.id ? event.seriesResults[team.id] : team.score ?? '-'}}</span>
            <span v-else>({{ team.score ?? '-' }})</span>
          </div>
        </div>
      </div>
    </div>
    <div class="szw-flex szw-flex-col szw-gap-2" v-else>
      <!-- TEAMS & RESULT SECTION -->
      <div class="szw-flex szw-flex-col szw-h-[60px] szw-w-full szw-justify-center szw-items-center">
        <!-- TEAM -->
        <div class="szw-flex szw-flex-row szw-w-full szw-gap-[10px] szw-h-[32px] szw-justify-center szw-items-center"
          v-for="team of event.competitors" :key="team.id">
          <div class="szw-h-[20px] szw-w-[20px]">
            <img v-if="team.country?.logo?.small" class="szw-h-5 szw-w-5 szw-rounded-[8px]"
              :src="team.country.logo.small" alt="team" />
            <fallback-logo v-else :text="team.abbreviation" />
          </div>

          <span :class="[
            'szw-text-xs szw-mr-auto szw-overflow-hidden szw-max-h-6 szw-whitespace-nowrap',
            { 'szw-font-semibold': event.winnerId ? event.winnerId === team.id : team.name !== 'Bye', 'szw-max-w-36': isSmallWidgetContainerSize }
          ]" style="text-overflow: ellipsis">
            {{ shortNameSingle(team.name) }}
          </span>
          <div v-if="event.periodScores" class="szw-flex szw-justify-center szw-items-center szw-gap-[4px]">
            <div v-for="period of event.periodScores" :key="period.number"
              :class="[staticClasses, getDynamicClass(period, team.qualifier)]">
              {{ period.score[team.qualifier] }}
            </div>
          </div>
          <div v-else :class="[staticClasses]">-</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import FallbackLogo from '@/__core__/components/FallbackLogo.vue'
import { computed, inject } from 'vue'
import { loc } from '@/__core__/localization/localization'
import { shortNameSingle } from '@/widgets/tennis-statistics/Statistics.helpers'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import { useAdjustedStatus } from '@/__core__/composables/useAdjustedStatus'

const langId = inject('langId', '')
const props = defineProps({
  sportType: String,
  laneItem: Object,
  laneItemIndex: Number,
  laneIndex: Number,
  laneItemStyle: Object,
  hasBracketLines: Boolean,
  hasThirdPlaceFinal: Boolean,
  laneItemLineStyle: Object,
  isSmallWidgetContainerSize: Boolean,

  /** @type {{
   *   formatDate: (startTime: string) => string
   *   getTime: (startTime: Date) => string
   * }} */
  helpers: {
    type: Object,
    default: () => ({
      formatDate: startTime => startTime,
      getTime: (date = new Date()) => `${date.getTime()}`
    })
  }
})

/* -- Emitters -- */
const emit = defineEmits(['lane-item-click'])

const isSingleMatch = computed(() => props.laneItem.events?.length === 1)

const event = computed(() => {
  const events = props.laneItem.events

  if (!Array.isArray(events) || !events.length) return getFallbackEvent()

  const scoreableEvents = events.filter(isScoreable)
  const { aggregatedTeams, aggregatedWinnerId } = aggregateEventScores(scoreableEvents)

  const base = findBaseEvent(events)
  const allEventsEnded = events.every(isScoreable)

  return {
    ...base,
    allEventsEnded,
    aggregatedTeams: props.sportType === 'basketball' ? base.teams : aggregatedTeams,
    winnerId: aggregatedWinnerId || base.winnerId,
    isSingleMatch: isSingleMatch.value,
    seriesResults: props.laneItem.seriesResults
  }
})

// -- Helper Functions --

function isScoreable(event) {
  return event.matchStatus && event.matchStatus !== '-'
}

function getFallbackEvent() {
  return {
    live: false,
    matchStatus: 'TBD',
    winnerId: '',
    aggregatedTeams: null,
    allEventsEnded: false,
    teams: [getTBDTeam(), getTBDTeam()]
  }
}

function getTBDTeam() {
  return {
    name: 'TBD',
    shortName: 'TBD',
    logoUrl: '',
    score: null,
    penaltyScore: null,
    virtual: true
  }
}

function findBaseEvent(events) {
  return (
    events.find(e => e.live) ||
    [...events].reverse().find(isScoreable) ||
    events[0]
  )
}

function aggregateEventScores(events) {
  if (!events.length) return { aggregatedTeams: null, aggregatedWinnerId: '' }

  const teamMap = new Map()

  for (const event of events) {
    const penaltyWinnerId = determinePenaltyWinner(event.teams)

    for (const team of event.teams) {
      const existing = teamMap.get(team.id) || createTeamEntry(team)
      existing.score += team.score || 0
      if (team.penaltyScore !== null) {
        existing.penaltyScore = (existing.penaltyScore ?? 0) + team.penaltyScore
      }

      if (team.id === penaltyWinnerId) {
        existing.score += 1
      }

      teamMap.set(team.id, existing)
    }
  }

  const aggregatedTeams = Array.from(teamMap.values())
  const aggregatedWinnerId = determineAggregateWinner(aggregatedTeams)

  return { aggregatedTeams, aggregatedWinnerId }
}

function determinePenaltyWinner(teams) {
  const [teamA, teamB] = teams
  if (
    teamA.penaltyScore !== null &&
    teamB.penaltyScore !== null &&
    teamA.penaltyScore !== teamB.penaltyScore
  ) {
    return teamA.penaltyScore > teamB.penaltyScore ? teamA.id : teamB.id
  }
  return null
}

function createTeamEntry(team) {
  return {
    id: team.id,
    name: team.name,
    shortName: team.shortName,
    logoUrl: team.logoUrl,
    score: 0,
    penaltyScore: null,
    virtual: team.virtual
  }
}

function determineAggregateWinner(teams) {
  if (teams.length !== 2) return ''
  const [a, b] = teams

  if (a.score > b.score) {
    return a.id
  } else if (b.score > a.score) {
    return b.id
  }
  
  return ''  // Return empty if scores are equal
}


const emitLaneItemClick = laneItem => emit('lane-item-click', { events: laneItem.events, activeEvent: event.value })

const adjustedStatus = useAdjustedStatus(event, loc, langId)

// Static classes for all periods
const staticClasses =
  'szw-flex szw-justify-center szw-items-center szw-p-[4px] szw-text-xs szw-w-[20px] szw-h-[24px] szw-rounded-[4px] szw-bg-section-static'

// Compute dynamic classes for a period
const getDynamicClass = (period, qualifier) => {
  const opponent = qualifier === 'home' ? 'away' : 'home';
  return {
    'szw-font-semibold': period.score[qualifier] > period.score[opponent]
  }
}
</script>

<style lang="scss" scoped>
.ko-bracket-y-line {
  width: 0.75rem;
  height: 106px;
  border: 2px solid var(--szw-color-tertiary);
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  border-left: 0;
  position: absolute;
  top: -6px;
  left: -1.5rem;
}

.ko-bracket-x-line {
  width: 0.75rem;
  height: 2px;
  background: var(--szw-color-tertiary);
  position: absolute;
  top: 50%;
  left: -0.75rem;
  transform: translateY(-2px);
}
</style>
